import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { configService, BoothConfig, HourlyPackage } from '../services/ConfigService';
import { API_BASE_URL } from '../config/api';
import RiyalSymbol from './RiyalSymbol';
import '../styles/BookingNew.css';

interface BookingBaseProps {
  boothSlug: string;
}

function BookingBase({ boothSlug }: BookingBaseProps) {
  const navigate = useNavigate();
  const [booth, setBooth] = useState<BoothConfig | null>(null);
  const [bookingSystem, setBookingSystem] = useState<'hourly' | 'perPhoto' | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [hourlyPackages, setHourlyPackages] = useState<HourlyPackage[]>([]);
  const [ticketConfig, setTicketConfig] = useState<any>(null);
  const [features, setFeatures] = useState<any>(null);
  const [minHourlyPrice, setMinHourlyPrice] = useState<number>(0);
  const [config, setConfig] = useState<any>(null);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  const [cityFee, setCityFee] = useState<number>(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    eventType: '',
    eventLocation: '',
    date: '',
    time: '',
    hours: 1,
    // Removed ticketCount - no extra tickets needed
  });

  useEffect(() => {
    const configData = configService.getConfig();
    const currentBooth = configData.booths.find(b => b.slug === boothSlug);
    
    if (!currentBooth) {
      navigate('/');
      return;
    }

    setConfig(configData);
    setBooth(currentBooth);
    
    // استخدام الأسعار المخصصة للكابينة إن وجدت
    const boothHourlyPackages = configService.getHourlyPackages(boothSlug);
    const boothTicketConfig = configService.getTicketSystemConfig(boothSlug);
    
    setHourlyPackages(boothHourlyPackages);
    setTicketConfig(boothTicketConfig);
    setFeatures(configData.features);
    
    // حساب أقل سعر للساعات
    const enabledPackages = boothHourlyPackages;
    if (enabledPackages.length > 0) {
      const lowestPrice = Math.min(...enabledPackages.map(pkg => pkg.price));
      setMinHourlyPrice(lowestPrice);
    }
  }, [boothSlug, navigate]);

  // جلب المدن المتاحة
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cities/active`);
        
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities);
          
          // تعيين المدينة الافتراضية
          const defaultCity = data.cities.find((city: any) => city.isDefault);
          if (defaultCity) {
            setSelectedCity(defaultCity);
            setCityFee(defaultCity.additionalPrice);
            setFormData(prev => ({
              ...prev,
              city: defaultCity._id
            }));
          }
        } else {
          console.error('Failed to fetch cities');
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    fetchCities();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = e.target.value;
    const city = cities.find(c => c._id === cityId);
    
    setSelectedCity(city);
    setCityFee(city ? city.additionalPrice : 0);
    setFormData(prev => ({
      ...prev,
      city: cityId
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // تحديد السعر حسب النظام المختار
    let totalAmount = 0;
    let hours = 0;
    
    if (bookingSystem === 'hourly') {
      const selectedPackage = hourlyPackages.find(pkg => 
        selectedPlan === `${pkg.hours}hours`
      );
      if (selectedPackage) {
        totalAmount = selectedPackage.price;
        hours = selectedPackage.hours;
      }
    } else {
      totalAmount = configService.calculateTicketSystemPrice(0, boothSlug); // No extra tickets
    }
    
    // إضافة رسوم المدينة
    totalAmount += cityFee;
    
    const bookingData = {
      boothType: boothSlug, // القيمة الداخلية: "classic" أو "modern" للAPI
      boothDisplayName: booth?.name, // الاسم المعروض من لوحة التحكم للإيميلات والعرض
      bookingType: bookingSystem,
      customerName: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: selectedCity?._id, // إرسال ID المدينة من قاعدة البيانات
      cityName: selectedCity?.name, // إرسال اسم المدينة
      cityFee: cityFee,
      eventType: formData.eventType, // إضافة نوع المناسبة
      eventDate: formData.date,
      eventTime: formData.time,
      eventLocation: formData.eventLocation,
      hours: hours,
      extraTickets: 0, // No extra tickets
      totalAmount: totalAmount,
      totalTickets: bookingSystem === 'perPhoto' ? ticketConfig.freeTickets : 0
    };
    
    // حفظ البيانات في localStorage
    localStorage.setItem('bookingData', JSON.stringify(bookingData));
    
    if (bookingSystem === 'perPhoto') {
      // الذهاب لصفحة تصميم التذاكر أولاً
      navigate('/ticket-design');
    } else {
      // الذهاب مباشرة لصفحة الدفع للحجز بالساعات
      navigate('/payment');
    }
  };

  if (!booth) {
    return <div>جاري التحميل...</div>;
  }

  const isHourlyEnabled = configService.isHourlySystemEnabled();
  const isTicketEnabled = configService.isTicketSystemEnabled();

  return (
    <div className="booking-page">
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/home">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <ul className="nav-menu">
            <li><Link to="/home">الرئيسية</Link></li>
            <li><a href="#contact">اتصل بنا</a></li>
          </ul>
        </div>
      </nav>

      <motion.section 
        className="booking-hero"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h1>لقد اخترت كابينة {booth.name}</h1>
        
        {!bookingSystem ? (
          <div className="booking-system-selection">
            <h2>اختر نظام الحجز المناسب لك</h2>
            <div className="system-options">
              <div className={`system-card ${!isHourlyEnabled ? 'disabled' : ''}`}>
                <div className="system-card-header">
                  <h3 className="system-name">نظام الساعات</h3>
                  <p className="system-subtitle">صور مفتوحة</p>
                  <div className="system-price-label">يبدأ من:</div>
                  <div className="system-price">
                    {(() => {
                      const cheapestPackage = hourlyPackages
                        .filter((pkg: HourlyPackage) => pkg.enabled)
                        .sort((a: HourlyPackage, b: HourlyPackage) => a.price - b.price)[0];
                      
                      if (cheapestPackage?.originalPrice && cheapestPackage.originalPrice > cheapestPackage.price) {
                        return (
                          <>
                            <span className="original-price">
                              {cheapestPackage.originalPrice.toLocaleString('en-US')}
                            </span>
                            <span className="discounted-price">
                              {cheapestPackage.price.toLocaleString('en-US')}
                            </span>
                          </>
                        );
                      }
                      return minHourlyPrice.toLocaleString('en-US');
                    })()}
                    <RiyalSymbol size={35} />
                  </div>
                </div>
                <div className="system-card-body">
                  <ul className="system-features">
                    {features?.hourlyFeatures.map((feature: string, index: number) => (
                      <li key={index} className={!isHourlyEnabled ? 'disabled-item' : ''}>{feature}</li>
                    ))}
                  </ul>
                  <button 
                    className="system-select-btn"
                    onClick={() => isHourlyEnabled && setBookingSystem('hourly')}
                    disabled={!isHourlyEnabled}
                  >
                    اختر هذا النظام
                  </button>
                </div>
                {!isHourlyEnabled && <div className="disabled-overlay">غير متاح حالياً</div>}
              </div>

              <div className={`system-card ${!isTicketEnabled ? 'disabled' : ''}`}>
                <div className="system-card-header">
                  <h3 className="system-name">نظام التذاكر</h3>
                  <p className="system-subtitle">دفع لكل صورة</p>
                  <div className="system-price-label">يبدأ من:</div>
                  <div className="system-price">
                    {(() => {
                      if (ticketConfig?.originalInstallationFee && ticketConfig.originalInstallationFee > ticketConfig.installationFee) {
                        return (
                          <>
                            <span className="original-price">
                              {ticketConfig.originalInstallationFee.toLocaleString('en-US')}
                            </span>
                            <span className="discounted-price">
                              {ticketConfig.installationFee.toLocaleString('en-US')}
                            </span>
                          </>
                        );
                      }
                      return ticketConfig?.installationFee.toLocaleString('en-US') || '2,500';
                    })()}
                    <RiyalSymbol size={35} />
                  </div>
                </div>
                <div className="system-card-body">
                  <ul className="system-features">
                    {features?.ticketFeatures.map((feature: string, index: number) => (
                      <li key={index} className={!isTicketEnabled ? 'disabled-item' : ''}>{feature}</li>
                    ))}
                  </ul>
                  <button 
                    className="system-select-btn"
                    onClick={() => isTicketEnabled && setBookingSystem('perPhoto')}
                    disabled={!isTicketEnabled}
                  >
                    اختر هذا النظام
                  </button>
                </div>
                {!isTicketEnabled && <div className="disabled-overlay">غير متاح حالياً</div>}
              </div>
            </div>
          </div>
        ) : (
          <motion.div 
            className="booking-form-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <button 
              className="back-button"
              onClick={() => {
                setBookingSystem(null);
                setSelectedPlan('');
              }}
            >
              رجوع لاختيار النظام
            </button>

            <form onSubmit={handleSubmit} className="booking-form">
              <h2>
                {bookingSystem === 'hourly' ? 'حجز بنظام الساعات' : 'حجز بنظام التذاكر'}
              </h2>

              {bookingSystem === 'hourly' && (
                <>
                  <div className="plan-selection">
                    <h3>اختر مدة الحجز</h3>
                    <div className="plan-options">
                      {hourlyPackages.map(pkg => (
                        <label key={pkg.hours} className={`plan-option ${selectedPlan === `${pkg.hours}hours` ? 'selected' : ''} ${!pkg.enabled ? 'disabled' : ''}`}>
                          <input
                            type="radio"
                            name="plan"
                            value={`${pkg.hours}hours`}
                            checked={selectedPlan === `${pkg.hours}hours`}
                            onChange={(e) => pkg.enabled && setSelectedPlan(e.target.value)}
                            disabled={!pkg.enabled}
                          />
                          <div className="plan-content">
                            <h4>{pkg.hours} ساعات</h4>
                            {pkg.originalPrice && pkg.originalPrice > pkg.price ? (
                              <div className="price-with-discount">
                                <p className="original-price">
                                  {pkg.originalPrice.toLocaleString('en-US')}
                                  <RiyalSymbol size={18} />
                                </p>
                                <p className="discounted-price">
                                  {pkg.price.toLocaleString('en-US')}
                                  <RiyalSymbol size={18} />
                                </p>
                                {pkg.discount && (
                                  <span className="discount-badge">{pkg.discount}% خصم</span>
                                )}
                              </div>
                            ) : (
                              <p>
                                {pkg.price.toLocaleString('en-US')}
                                <RiyalSymbol size={18} />
                              </p>
                            )}
                            {!pkg.enabled && <span className="disabled-badge">غير متاح</span>}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="form-section">
                <h3>معلومات الحجز</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>الاسم الكامل</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>رقم الجوال</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="05XXXXXXXX"
                      pattern="^05[0-9]{8}$"
                      title="يجب أن يبدأ الرقم بـ 05 ويتكون من 10 أرقام"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>البريد الإلكتروني</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="example@email.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>المدينة</label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleCityChange}
                      required
                    >
                      <option value="">اختر المدينة</option>
                      {cities.map((city) => (
                        <option key={city._id} value={city._id}>
                          {city.name} {city.additionalPrice > 0 && `(+${city.additionalPrice} ريال)`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>نوع المناسبة</label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">اختر نوع المناسبة</option>
                      <option value="wedding">زفاف</option>
                      <option value="engagement">خطوبة</option>
                      <option value="birthday">عيد ميلاد</option>
                      <option value="graduation">تخرج</option>
                      <option value="corporate">مناسبة شركات</option>
                      <option value="other">أخرى</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>مكان المناسبة</label>
                    <input
                      type="text"
                      name="eventLocation"
                      value={formData.eventLocation}
                      onChange={handleInputChange}
                      placeholder="مثال: قاعة الأفراح، فندق..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>تاريخ المناسبة</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>وقت بدء المناسبة</label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                </div>
              </div>

              <div className="price-summary">
                <h3>ملخص الحجز</h3>
                <div className="price-details">
                  {formData.date && (
                    <div className="price-row">
                      <span>تاريخ المناسبة:</span>
                      <span>{new Date(formData.date).toLocaleDateString('ar-SA')}</span>
                    </div>
                  )}
                  {formData.time && (
                    <div className="price-row">
                      <span>وقت بدء المناسبة:</span>
                      <span>{formData.time}</span>
                    </div>
                  )}
                  {bookingSystem === 'hourly' && hourlyPackages.map(pkg => {
                    if (selectedPlan === `${pkg.hours}hours`) {
                      return (
                        <React.Fragment key={pkg.hours}>
                          <div className="price-row">
                            <span>مدة الحجز:</span>
                            <span>{pkg.hours} ساعات</span>
                          </div>
                          <div className="price-row">
                            <span>صور مفتوحة</span>
                            <span>غير محدودة</span>
                          </div>
                          <div className="price-row">
                            <span>سعر الكابينة:</span>
                            <span>
                              {pkg.price.toLocaleString('en-US')}
                              <RiyalSymbol size={16} />
                            </span>
                          </div>
                          {cityFee > 0 && (
                            <div className="price-row">
                              <span>رسوم المدينة ({selectedCity?.name}):</span>
                              <span>
                                {cityFee.toLocaleString('en-US')}
                                <RiyalSymbol size={16} />
                              </span>
                            </div>
                          )}
                          <div className="price-row total">
                            <span>المجموع:</span>
                            <span>
                              {(pkg.price + cityFee).toLocaleString('en-US')}
                              <RiyalSymbol size={16} />
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}

                  {bookingSystem === 'perPhoto' && (
                    <>
                      <div className="price-row">
                        <span>رسوم التركيب:</span>
                        <span>
                          {ticketConfig.installationFee.toLocaleString('en-US')}
                          <RiyalSymbol size={16} />
                        </span>
                      </div>
                      <div className="price-row">
                        <span>تذاكر مجانية:</span>
                        <span>{ticketConfig.freeTickets} تذكرة</span>
                      </div>
                      <div className="price-row">
                        <span>إجمالي التذاكر:</span>
                        <span>{ticketConfig.freeTickets} تذكرة</span>
                      </div>
                      {cityFee > 0 && (
                        <div className="price-row">
                          <span>رسوم المدينة ({selectedCity?.name}):</span>
                          <span>
                            {cityFee.toLocaleString('en-US')}
                            <RiyalSymbol size={16} />
                          </span>
                        </div>
                      )}
                      <div className="price-row total">
                        <span>المجموع:</span>
                        <span>
                          {(configService.calculateTicketSystemPrice(0, boothSlug) + cityFee).toLocaleString('en-US')}
                          <RiyalSymbol size={16} />
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={bookingSystem === 'hourly' && !selectedPlan}
              >
                {bookingSystem === 'perPhoto' ? 'التالي - تصميم التذاكر' : 'تأكيد الحجز'}
              </button>
            </form>
          </motion.div>
        )}
      </motion.section>
    </div>
  );
}

export default BookingBase;